import type { AiOperationalForecast, AiOperationalRequest } from '../../../types/aiOperational'
import type { AiOperationalContextWindow, AiRetrievedRecord } from '../context/operationalRetrieval'

export interface OperationalForecastInput {
  request: AiOperationalRequest
  context: AiOperationalContextWindow
  nowIso?: string
  maxForecasts?: number
}

const DAY_MS = 24 * 60 * 60 * 1000

const stableId = (prefix: string, parts: string[]) =>
  `${prefix}_${parts.join('_').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 96)}`

const isComplete = (record: AiRetrievedRecord) =>
  record.status === 'COMPLETE' || record.status === 'COMPLETED' || record.status === 'NOT_REQUIRED' || record.progress === 100

const isActive = (record: AiRetrievedRecord) =>
  record.status === 'IN_PROGRESS' || record.status === 'ONGOING'

const parseDueMs = (value: string | null | undefined) => {
  if (!value) return null
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : null
}

const createForecast = (
  patch: Omit<AiOperationalForecast, 'generatedAtIso'>,
  generatedAtIso: string,
): AiOperationalForecast => ({
  ...patch,
  generatedAtIso,
})

export const buildOperationalForecasts = (input: OperationalForecastInput): AiOperationalForecast[] => {
  const generatedAtIso = input.nowIso ?? new Date().toISOString()
  const nowMs = Date.parse(generatedAtIso)
  const records = [...input.context.primary, ...input.context.supporting]
  const forecasts: AiOperationalForecast[] = []

  const driftRecords = records.filter((record) => {
    if (isComplete(record)) return false
    const dueMs = parseDueMs(record.dueDate)
    if (dueMs == null) return false
    const daysUntilDue = (dueMs - nowMs) / DAY_MS
    const progress = typeof record.progress === 'number' ? record.progress : isActive(record) ? 50 : 0
    return daysUntilDue < 0 || (daysUntilDue <= 3 && progress < 80)
  })
  if (driftRecords.length > 0) {
    const overdue = driftRecords.some((record) => {
      const dueMs = parseDueMs(record.dueDate)
      return dueMs != null && dueMs < nowMs
    })
    forecasts.push(createForecast({
      id: stableId('forecast_drift', driftRecords.slice(0, 4).map((record) => record.id)),
      kind: 'TIMELINE_DRIFT',
      severity: overdue ? 'CRITICAL' : 'WATCH',
      summary: `${driftRecords.slice(0, 3).map((record) => record.title).join(', ')} ${overdue ? 'is overdue or stale' : 'is close to its date with incomplete progress'}.`,
      evidenceRecordIds: driftRecords.slice(0, 6).map((record) => record.id),
      recommendedAction: 'Review dates, blockers, and next owner before adding new work.',
    }, generatedAtIso))
  }

  const readinessRecords = records.filter((record) =>
    record.reasons.includes('dependency participant') || record.reasons.includes('completion gate'))
  const pulseBlockers = input.context.pulseHints.filter((hint) => /block|shortage|fail|risk|critical/i.test(`${hint.severity ?? ''} ${hint.summary}`))
  if (readinessRecords.length || pulseBlockers.length) {
    forecasts.push(createForecast({
      id: stableId('forecast_readiness', [
        ...readinessRecords.slice(0, 3).map((record) => record.id),
        ...pulseBlockers.slice(0, 3).map((hint) => hint.id),
      ]),
      kind: 'READINESS_BLOCKER',
      severity: pulseBlockers.some((hint) => /critical/i.test(`${hint.severity ?? ''} ${hint.summary}`)) ? 'CRITICAL' : 'WATCH',
      summary: 'Readiness may be blocked by dependencies, proof/location gates, resource shortages, or recent Pulse risk.',
      evidenceRecordIds: [
        ...readinessRecords.map((record) => record.id),
        ...pulseBlockers.map((hint) => hint.targetId).filter((id): id is string => Boolean(id)),
      ].slice(0, 8),
      recommendedAction: 'Resolve blockers or prepare a reviewed proposal before marking work complete.',
    }, generatedAtIso))
  }

  const activeRecords = records.filter(isActive)
  if (activeRecords.length >= 3) {
    forecasts.push(createForecast({
      id: stableId('forecast_workload', activeRecords.slice(0, 4).map((record) => record.id)),
      kind: 'WORKLOAD_CONCENTRATION',
      severity: activeRecords.length >= 5 ? 'CRITICAL' : 'WATCH',
      summary: `${activeRecords.length} retrieved records are already active, which can increase drift and coordination risk.`,
      evidenceRecordIds: activeRecords.slice(0, 8).map((record) => record.id),
      recommendedAction: 'Sequence active work and avoid proposing parallel additions without a clear owner.',
    }, generatedAtIso))
  }

  const asksForOperationalArtifact = /\b(plan|report|meeting|automation|launch|timeline|checklist|brief|readiness)\b/i.test(input.request.prompt)
  if (asksForOperationalArtifact && records.length === 0) {
    forecasts.push(createForecast({
      id: stableId('forecast_missing_scope', [input.request.surface, input.request.mode]),
      kind: 'MISSING_STEP',
      severity: 'INFO',
      summary: 'No relevant workspace records were retrieved for this operational request.',
      evidenceRecordIds: [],
      recommendedAction: 'Ask one focused clarification or request a target record before drafting operational changes.',
    }, generatedAtIso))
  }

  return forecasts
    .sort((left, right) => {
      const rank = { CRITICAL: 3, WATCH: 2, INFO: 1 }
      return rank[right.severity] - rank[left.severity] || left.kind.localeCompare(right.kind)
    })
    .slice(0, input.maxForecasts ?? 5)
}
